import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { fetchRefills, fetchEndorsements, formatUsd } from "../lib/api";
import { useCart } from "../context/CartContext";
import { REFILLS_IDS } from "../constants/testIds";

const CATEGORY_GROUPS = [
  { label: "Compression", match: (c) => c.toLowerCase().includes("compression") || c.toLowerCase().includes("support") },
  { label: "Scar Care", match: (c) => c.toLowerCase().includes("scar") },
  { label: "Skincare", match: (c) => /barrier|hydration serum|regenerative|bruise/i.test(c) },
  { label: "Cooling & Bruise", match: (c) => /cooling|bruise/i.test(c) },
  { label: "Sun Protection", match: (c) => /uv/i.test(c) },
  { label: "Hydration", match: (c) => c === "Hydration" },
  { label: "Comfort & Care", match: (c) => /positioning|drain/i.test(c) },
];

function classifyCategory(cat) {
  for (const g of CATEGORY_GROUPS) if (g.match(cat)) return g.label;
  return "Other";
}

export default function RefillsPage() {
  const [refills, setRefills] = useState([]);
  const [endorsements, setEndorsements] = useState({});
  const [search, setSearch] = useState("");
  const [activeBrand, setActiveBrand] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const { addRefill } = useCart();

  useEffect(() => {
    fetchRefills().then(setRefills);
    fetchEndorsements().then(setEndorsements);
  }, []);

  const brands = useMemo(() => [...new Set(refills.map((r) => r.brand))].sort(), [refills]);
  const categories = useMemo(() => [...new Set(refills.map((r) => classifyCategory(r.category)))].sort(), [refills]);

  const filtered = useMemo(() => {
    return refills.filter((r) => {
      if (activeBrand && r.brand !== activeBrand) return false;
      if (activeCategory && classifyCategory(r.category) !== activeCategory) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.brand.toLowerCase().includes(s) || r.name.toLowerCase().includes(s) || r.category.toLowerCase().includes(s)))
          return false;
      }
      return true;
    });
  }, [refills, activeBrand, activeCategory, search]);

  const clearFilters = () => {
    setActiveBrand(null);
    setActiveCategory(null);
    setSearch("");
  };

  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="eyebrow">Individual Products</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">
            Refills, Standalone.
          </h1>
          <p className="text-[#6B5757] mt-6 leading-relaxed">
            Six weeks in? Six months in? Every branded item from every ALTA system is available on its own — Marena garments, Silagen silicone, Alastin serums, Charlotte Tilbury cryo, Augustinus Bader — ready to re-order the moment you need one.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-[#F0DFD0] bg-[#FBF6F1] sticky top-20 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-4 flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          <div className="relative w-full lg:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A08D8D]" />
            <input
              data-testid={REFILLS_IDS.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brand or product"
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-[#E5CDBB] focus:outline-none focus:border-[#2A1F1F]"
            />
          </div>
          <div className="flex-1 overflow-x-auto -mx-2 px-2">
            <div className="flex gap-2 whitespace-nowrap">
              {brands.map((b) => (
                <button
                  key={b}
                  data-testid={REFILLS_IDS.brandFilter(b)}
                  onClick={() => setActiveBrand(activeBrand === b ? null : b)}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                    activeBrand === b
                      ? "bg-[#2A1F1F] text-[#FEFBF9] border-[#2A1F1F]"
                      : "bg-white text-[#2A1F1F] border-[#E5CDBB] hover:border-[#2A1F1F]"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          {(activeBrand || activeCategory || search) && (
            <button
              data-testid={REFILLS_IDS.clearFilters}
              onClick={clearFilters}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F]"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-4 flex gap-2 flex-wrap">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={REFILLS_IDS.categoryFilter(c)}
              onClick={() => setActiveCategory(activeCategory === c ? null : c)}
              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                activeCategory === c
                  ? "bg-[#B85C56] text-[#FBF6F1] border-[#B85C56]"
                  : "bg-transparent text-[#6B5757] border-transparent hover:text-[#2A1F1F]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
        <div className="mb-8 text-sm text-[#6B5757]">
          Showing <span className="font-medium text-[#2A1F1F]">{filtered.length}</span> of {refills.length} products
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-serif text-2xl text-[#A08D8D]">No matches</p>
            <button onClick={clearFilters} className="btn-outline mt-6">Reset filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((r) => {
              const endorse = endorsements[r.brand];
              return (
                <div
                  key={r.id}
                  data-testid={REFILLS_IDS.card(r.id)}
                  className="group bg-white border border-[#F0DFD0] overflow-hidden flex flex-col"
                >
                  <Link to={`/refills/${r.id}`} className="block">
                    <div className="aspect-[4/3] overflow-hidden bg-[#FDF3ED]">
                      <img
                        src={r.image} alt={r.name} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#B85C56]">{r.brand}</div>
                      <div className="font-mono text-xs text-[#6B5757]">{formatUsd(r.price)}</div>
                    </div>
                    <Link to={`/refills/${r.id}`} className="block">
                      <h3 className="font-serif text-base mt-2 leading-tight text-[#2A1F1F] hover:text-[#B85C56] transition-colors">
                        {r.name}
                      </h3>
                    </Link>
                    <div className="text-[10px] uppercase tracking-widest text-[#6B5757] mt-1">{r.category}</div>
                    {endorse && (
                      <div className="mt-4 border-l-2 border-[#B89B66] pl-3">
                        <p className="text-[11px] italic text-[#6B5757] leading-snug line-clamp-2">"{endorse.quote}"</p>
                        <div className="font-mono text-[9px] tracking-widest uppercase text-[#B85C56] mt-1">
                          — {endorse.author.split(",")[0]}
                        </div>
                      </div>
                    )}
                    <button
                      data-testid={REFILLS_IDS.addToCart(r.id)}
                      onClick={() => {
                        addRefill(r, 1);
                        toast.success(`${r.brand} · ${r.name} added`);
                      }}
                      className="mt-5 inline-flex items-center justify-center gap-2 w-full py-2 border border-[#2A1F1F] text-xs uppercase tracking-[0.2em] hover:bg-[#2A1F1F] hover:text-[#FEFBF9] transition-colors"
                    >
                      <Plus className="w-3 h-3" /> Add to Cart
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
